import apiClient from "./client";

import type {
  OrgStructureLevel,
  OrgStructureNode,
  ScopeVerdict,
} from "@/lib/org-structure-tree";
import type { components } from "@/types/api.generated";

export type {
  OrgConformity,
  OrgConformityStatus,
  OrgNameSource,
  OrgNameStatus,
  OrgRelationStatus,
  OrgStructureLevel,
  OrgStructureNode,
  ScopeVerdict,
  UnitOrigin,
} from "@/lib/org-structure-tree";

/** A coverage state calculated by the read-only DE→PARA query. */
export type CoverageState = "mapeado" | "sem_mapeamento" | "sem_atividade";

export interface ConfigCell {
  domain: string;
  table_name: string;
  field_name: string;
  sscui: string | null;
  target_field: string | null;
  value_count: number;
  activity_name: string | null;
  priority: string | null;
  cookbook_group: string | null;
  source_description: string | null;
  mapping_note: string | null;
  coverage: CoverageState;
}

export interface ConfigCellsResponse {
  artifact_id: string | null;
  execution_id: string | null;
  source_system: "ecc" | "s4hana" | null;
  items: ConfigCell[];
}

function artifactParams(artifactId?: string): Record<string, string> {
  return artifactId ? { artifact_id: artifactId } : {};
}

export async function listConfigCells(
  projectId: string,
  artifactId?: string,
): Promise<ConfigCellsResponse> {
  const response = await apiClient.get<ConfigCellsResponse>(
    `/api/projects/${projectId}/configuration-server/cells`,
    { params: artifactParams(artifactId) },
  );
  return response.data;
}

export interface OrgStructureResponse {
  artifact_id: string | null;
  execution_id: string | null;
  source_system: "ecc" | "s4hana" | null;
  levels: OrgStructureLevel[];
}

export type OrgStructureSyncStatus = components["schemas"]["OrgStructureSyncStatusResponse"];
export type OrgStructureSyncResult = components["schemas"]["OrgStructureSyncResponse"];

export async function getOrgStructureSyncStatus(projectId: string): Promise<OrgStructureSyncStatus> {
  const response = await apiClient.get<OrgStructureSyncStatus>(
    `/api/projects/${projectId}/configuration-server/org-structure/sync-status`,
  );
  return response.data;
}

export async function syncOrgStructure(projectId: string): Promise<OrgStructureSyncResult> {
  const response = await apiClient.post<OrgStructureSyncResult>(
    `/api/projects/${projectId}/configuration-server/org-structure/sync`,
  );
  return response.data;
}

function assertNodeConformity(node: OrgStructureNode): void {
  if (node.conformity == null) {
    throw new Error("Org structure node is missing required conformity");
  }
}

function assertOrgStructureContract(response: OrgStructureResponse): OrgStructureResponse {
  for (const level of response.levels) {
    for (const node of level.nodes) {
      assertNodeConformity(node);
    }
  }
  return response;
}

export interface DeParaColumn {
  field_name: string;
  target_field: string | null;
  source_description: string | null;
}

export interface DeParaRecordsPage {
  domain: string;
  table_name: string;
  columns: DeParaColumn[];
  /** Aligned to `columns` by index. */
  records: Array<Array<string | null>>;
  total: number;
  page: number;
  limit: number;
}

/** ck_configuration_table_status — 'data' é a única com registros reais. */
export type ConfigTableStatus =
  | "data"
  | "empty"
  | "structural_error"
  | "not_found"
  | "not_authorized"
  | "error";

export interface ConfigTable {
  domain: string;
  table_name: string;
  field_count: number;
  mapped_field_count: number;
  record_count: number;
  sscuis: string[];
  activity_names: string[];
  priority: string | null;
  coverage: CoverageState;
  status: ConfigTableStatus;
}

export interface ConfigTablesQuery {
  artifact_id?: string;
  page?: number;
  limit?: number;
  domain?: string;
  coverage?: string;
  search?: string;
  status?: string;
}

export interface ConfigTablesResponse {
  artifact_id: string | null;
  execution_id: string | null;
  source_system: "ecc" | "s4hana" | null;
  items: ConfigTable[];
  total: number;
  page: number;
  limit: number;
}

/** The modal is fixed at 10 records and offers no page-size selector. */
export const DEPARA_RECORDS_PAGE_SIZE = 10;

export async function listConfigTableRecords(
  projectId: string,
  tableName: string,
  query: { domain: string; artifact_id?: string; page?: number; activity_code?: string },
): Promise<DeParaRecordsPage> {
  const params: Record<string, string> = {
    domain: query.domain,
    page: String(query.page ?? 1),
    limit: String(DEPARA_RECORDS_PAGE_SIZE),
  };
  if (query.artifact_id) params.artifact_id = query.artifact_id;
  if (query.activity_code) params.activity_code = query.activity_code;
  // SAP namespace tables are named like /BEV1/RBVBAK — without encoding, the
  // slashes would be read as extra path segments.
  const response = await apiClient.get<DeParaRecordsPage>(
    `/api/projects/${projectId}/configuration-server/tables/${encodeURIComponent(tableName)}/records`,
    { params },
  );
  return response.data;
}

export async function listConfigTables(
  projectId: string,
  query: ConfigTablesQuery = {},
): Promise<ConfigTablesResponse> {
  const params: Record<string, string> = {};
  if (query.artifact_id) params.artifact_id = query.artifact_id;
  if (query.page != null) params.page = String(query.page);
  if (query.limit != null) params.limit = String(query.limit);
  if (query.domain) params.domain = query.domain;
  if (query.coverage) params.coverage = query.coverage;
  if (query.search) params.search = query.search;
  if (query.status) params.status = query.status;
  const response = await apiClient.get<ConfigTablesResponse>(
    `/api/projects/${projectId}/configuration-server/tables`,
    { params },
  );
  return response.data;
}

export async function listOrgStructure(
  projectId: string,
  artifactId?: string,
): Promise<OrgStructureResponse> {
  const response = await apiClient.get<OrgStructureResponse>(
    `/api/projects/${projectId}/configuration-server/org-structure`,
    { params: artifactParams(artifactId) },
  );
  return assertOrgStructureContract(response.data);
}

export interface ScopeVerdictAffectedUnit {
  item_type: string;
  item_key: string;
}

export interface ScopeVerdictResult {
  verdict: ScopeVerdict;
  affected: ScopeVerdictAffectedUnit[];
}

/**
 * Aprova ou tira do escopo uma unidade. A cascata é sempre recalculada no
 * servidor — nunca mande a prévia calculada localmente, ela existe só para
 * o modal informar antes de confirmar (ver previewCascade, Task 11).
 */
export async function setScopeVerdict(
  projectId: string,
  itemType: string,
  itemKey: string,
  verdict: Exclude<ScopeVerdict, "pending">,
): Promise<ScopeVerdictResult> {
  const response = await apiClient.post<ScopeVerdictResult>(
    `/api/projects/${projectId}/configuration-server/org-structure/scope-verdict`,
    { item_type: itemType, item_key: itemKey, verdict },
  );
  return response.data;
}

/**
 * Corpo de `attributes` para criação/edição de unidade manual — livre por
 * design (o backend valida por tipo em `org_unit_forms.py`, um campo por vez
 * aqui seria uma duplicação frágil da mesma regra). `null` só faz sentido na
 * EDIÇÃO: é como o consultor desfaz uma sobreposição (`jsonb_strip_nulls`).
 * `local_currency` saiu — não é mais gravável pelo consultor (#899); `subtype`
 * entrou para storage_location/shipping_point/purchasing_organization.
 */
export interface ManualUnitAttributes {
  name?: string | null;
  city?: string | null;
  country?: string | null;
  subtype?: string | null;
  parent_id?: string | null;
  /** Muitos-para-muitos (Distribution Channel, Division): os ids das Sales Organizations. */
  parent_ids?: string[] | null;
}

export async function createManualUnit(
  projectId: string,
  itemType: string,
  itemKey: string,
  attributes: ManualUnitAttributes,
): Promise<{ id: string }> {
  const response = await apiClient.post<{ id: string }>(
    `/api/projects/${projectId}/configuration-server/org-structure/units`,
    { item_type: itemType, item_key: itemKey, attributes },
  );
  return response.data;
}

export async function upsertUnitAttributes(
  projectId: string,
  itemType: string,
  itemKey: string,
  attributes: ManualUnitAttributes,
): Promise<void> {
  await apiClient.put(
    `/api/projects/${projectId}/configuration-server/org-structure/attributes`,
    { item_type: itemType, item_key: itemKey, attributes },
  );
}

export async function deleteManualUnit(projectId: string, unitId: string): Promise<void> {
  await apiClient.delete(
    `/api/projects/${projectId}/configuration-server/org-structure/units/${unitId}`,
  );
}

/** Uma aba do modal de detalhe: a tabela extraída onde a configuração aparece. */
export interface ActivityTable {
  domain: string;
  table_name: string;
  record_count: number;
  field_count: number;
  status: ConfigTableStatus;
}

/** O que o modal de detalhe lê, nos Nós 03 e 04. */
export interface ActivityTablesSummary {
  activity_code: string;
  name: string | null;
  priority: string | null;
  scope_item_count?: number;
  /** Só a SSCUI traz: o cabeçalho dos detalhes mostra subárea e países. */
  application_subarea?: string | null;
  countries?: string[];
  tables: ActivityTable[];
}

export interface SscuiActivityTablesResponse extends ActivityTablesSummary {
  application_subarea: string | null;
  countries: string[];
  scope_item_count: number;
}

export async function listSscuiActivityTables(
  projectId: string,
  activityCode: string,
  artifactId?: string,
): Promise<SscuiActivityTablesResponse> {
  const response = await apiClient.get<SscuiActivityTablesResponse>(
    `/api/projects/${projectId}/configuration-server/activities/${encodeURIComponent(activityCode)}/tables`,
    { params: artifactParams(artifactId) },
  );
  return response.data;
}

